import { ActionIcon, Button, Divider, Group, Paper, Stack, Tabs, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { IconPencil } from '@tabler/icons-react';
import { addDays, format, getISODay, isAfter, parse, startOfDay } from 'date-fns';
import { primaryColor } from '../../constants/theme';
import { TimePickerModal } from './TimePickerModal';
import { RecurringDates, getDefaultEndDate } from './RecurringDates';
import { SpecificDates } from './SpecificDates';
import { TopicPickerModal } from './TopicPickerModal';
import { ReviewAvailabilityModal } from './ReviewAvailabilityModal';
import { AppToast } from '../AppToast';
import {
  buildNewSlots,
  buildPayload,
  expandRecurringDates,
  filterConflictingSlots,
} from './availabilityHelpers';
import { getProfessionalAvailability, postProfessionalAvailability } from '../../services/AvailabilityService';
import { useTopic } from '../../context/TopicContext';
import { useApp } from '../../context/AppContext';

const snapToQuarter = (date: Date): Date => {
  const snapped = new Date(date);
  snapped.setMinutes(Math.ceil(date.getMinutes() / 15) * 15, 0, 0);
  return snapped;
};

const getDefaultStartTime = () => format(snapToQuarter(new Date()), 'HH:mm');
const getDefaultEndTime = () => {
  const d = snapToQuarter(new Date());
  d.setHours(d.getHours() + 1);
  return format(d, 'HH:mm');
};

type ActivePicker = 'start' | 'end' | null;

const toMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const countRecurringDates = (days: Set<number>, endDateStr: string): number => {
  if (days.size === 0) return 0;
  const end = parse(endDateStr, 'dd MMM yyyy', new Date());
  let current = startOfDay(new Date());
  let count = 0;
  while (!isAfter(current, end)) {
    if (days.has(getISODay(current))) count++;
    current = addDays(current, 1);
  }
  return count;
};

export const AddAvailabilityTab = ({ onSuccess }: { onSuccess?: () => void }) => {
  const { token, profile } = useApp();
  const { topics, fetchTopics } = useTopic();
  const [startTime, setStartTime] = useState(getDefaultStartTime);
  const [endTime, setEndTime] = useState(getDefaultEndTime);
  const [activePicker, setActivePicker] = useState<ActivePicker>(null);
  const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
  const [showTopicPicker, setShowTopicPicker] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [activeDateTab, setActiveDateTab] = useState<string | null>('specific');
  const [specificDates, setSpecificDates] = useState<string[]>([]);
  const [recurringDays, setRecurringDays] = useState<Set<number>>(new Set());
  const [recurringEndDate, setRecurringEndDate] = useState(getDefaultEndDate);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (token) {
      fetchTopics();
    }
  }, [token, fetchTopics]);

  const { data: existingAvailability } = useQuery({
    queryKey: ['availability', profile?.id],
    queryFn: () => getProfessionalAvailability(profile!.id, token!),
    enabled: !!profile?.id && !!token,
  });

  const { mutate: submitAvailability, isPending: isSubmitting } = useMutation({
    mutationFn: (payload: ReturnType<typeof buildPayload>) =>
      postProfessionalAvailability(payload, token!),
  });

  const resetForm = () => {
    setStartTime(getDefaultStartTime());
    setEndTime(getDefaultEndTime());
    setSelectedTopicIds([]);
    setSpecificDates([]);
    setRecurringDays(new Set());
    setRecurringEndDate(getDefaultEndDate());
    setActiveDateTab('specific');
    setShowReview(false);
  };

  const handleSubmit = () => {
    const selectedTopics = topics.filter((t) => selectedTopicIds.includes(t.id));
    const dates = activeDateTab === 'specific'
      ? specificDates
      : expandRecurringDates(recurringDays, recurringEndDate);

    const allSlots = buildNewSlots(selectedTopics, startTime, endTime, dates);
    const { validSlots, conflictCount } = filterConflictingSlots(allSlots, existingAvailability ?? null);

    if (validSlots.length === 0) {
      setShowReview(false);
      setToast({ message: 'All slots already exist for these topics and times.', variant: 'error' });
      return;
    }

    const payload = buildPayload(validSlots);
    submitAvailability(payload, {
      onSuccess: () => {
        const msg = conflictCount > 0
          ? `Availability set. ${conflictCount} duplicate slot${conflictCount !== 1 ? 's' : ''} for the same topic were skipped.`
          : 'Availability set successfully.';
        resetForm();
        setToast({ message: msg, variant: 'success' });
        onSuccess?.();
      },
      onError: () => {
        setShowReview(false);
        setToast({ message: 'Failed to set availability. Please try again.', variant: 'error' });
      },
    });
  };

  const handleConfirm = (time: string) => {
    if (activePicker === 'start') {
      setStartTime(time);
    } else if (activePicker === 'end') {
      setEndTime(time);
    }
    setActivePicker(null);
  };

  const startMinutes = toMinutes(startTime);
  const endMinutes = toMinutes(endTime);
  const isValid = endMinutes > startMinutes;
  const sessionDuration = endMinutes - startMinutes;

  const totalDates =
    activeDateTab === 'specific'
      ? specificDates.length
      : countRecurringDates(recurringDays, recurringEndDate);

  const slotsPerDay = (durationMinutes: number) =>
    durationMinutes > 0 ? Math.floor(sessionDuration / durationMinutes) : 0;

  const totalSlots = (durationMinutes: number) => slotsPerDay(durationMinutes) * totalDates;

  const grandTotalSlots = topics
    .filter((t) => selectedTopicIds.includes(t.id))
    .reduce((sum, t) => sum + totalSlots(t.durationMinutes ?? 30), 0);

  const topicSlots = topics
    .filter((t) => selectedTopicIds.includes(t.id))
    .map((t) => ({
      topic: t,
      slotsPerDay: slotsPerDay(t.durationMinutes ?? 30),
      totalSlots: totalSlots(t.durationMinutes ?? 30),
    }));

  return (
    <Stack gap="md">
      <Title order={4}>
        Time
      </Title>

      <Paper withBorder shadow="none" radius="sm" p="sm" style={{ cursor: 'pointer' }} onClick={() => setActivePicker('start')}>
        <Group justify="space-between" align="center">
          <Group gap={6}>
            <Text>Start Time:</Text>
            <Text c={primaryColor} fw={600}>
              {startTime}
            </Text>
          </Group>
          <ActionIcon variant="subtle" color="gray" aria-label="Edit start time" component="div">
            <IconPencil size={16} />
          </ActionIcon>
        </Group>
      </Paper>

      <Paper withBorder shadow="none" radius="sm" p="sm" style={{ cursor: 'pointer' }} onClick={() => setActivePicker('end')}>
        <Group justify="space-between" align="center">
          <Group gap={6}>
            <Text>End Time:</Text>
            <Text c={primaryColor} fw={600}>
              {endTime}
            </Text>
          </Group>
          <ActionIcon variant="subtle" color="gray" aria-label="Edit end time" component="div">
            <IconPencil size={16} />
          </ActionIcon>
        </Group>
      </Paper>

      <TimePickerModal
        opened={activePicker !== null}
        label={activePicker === 'start' ? 'Start Time' : 'End Time'}
        initialTime={activePicker === 'start' ? startTime : endTime}
        field={activePicker ?? 'start'}
        otherTime={activePicker === 'start' ? endTime : startTime}
        onClose={() => setActivePicker(null)}
        onConfirm={handleConfirm}
      />

      <Title order={4}>
        Dates
      </Title>

      <Tabs value={activeDateTab} onChange={setActiveDateTab} color={primaryColor} variant="pills">
        <Tabs.List
          style={(theme) => ({
            border: `1px solid ${theme.colors.gray[3]}`,
            borderRadius: theme.radius.sm,
            padding: 2,
            gap: 0,
          })}
        >
          <Tabs.Tab
            value="specific"
            style={{ flex: 1, borderRadius: 'var(--mantine-radius-sm)' }}
          >
            Specific Days
          </Tabs.Tab>
          <Tabs.Tab
            value="recurring"
            style={{ flex: 1, borderRadius: 'var(--mantine-radius-sm)' }}
          >
            Recurring
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="specific" pt="md">
          <SpecificDates selectedDates={specificDates} onDatesChange={setSpecificDates} />
        </Tabs.Panel>

        <Tabs.Panel value="recurring" pt="md">
          <RecurringDates
            selectedDays={recurringDays}
            onDaysChange={setRecurringDays}
            endDate={recurringEndDate}
            onEndDateChange={setRecurringEndDate}
          />
        </Tabs.Panel>
      </Tabs>

      <Title order={4}>
        Topics
      </Title>

      <Paper withBorder shadow="none" radius="sm" style={{ cursor: 'pointer', overflow: 'hidden' }} onClick={() => setShowTopicPicker(true)} data-testid="topics-section">
        {selectedTopicIds.length === 0 ? (
          <Group justify="space-between" align="center" p="sm">
            <Text c="dimmed">Select one or more topics</Text>
            <ActionIcon variant="subtle" color="gray" aria-label="Edit topics" component="div">
              <IconPencil size={16} />
            </ActionIcon>
          </Group>
        ) : (
          <>
            <Group justify="space-between" align="center" px="sm" pt="sm" pb="xs">
              <Text size="sm" c="dimmed" data-testid="topics-selected-count">
                {selectedTopicIds.length} {selectedTopicIds.length === 1 ? 'topic' : 'topics'} selected
              </Text>
              <ActionIcon variant="subtle" color="gray" aria-label="Edit topics" component="div">
                <IconPencil size={16} />
              </ActionIcon>
            </Group>
            {topics
              .filter((t) => selectedTopicIds.includes(t.id))
              .map((topic, index, arr) => {
                const color = topic.colour ?? primaryColor;
                return (
                  <div key={topic.id} data-testid={`topic-row-${topic.id}`}>
                    {index > 0 && <Divider />}
                    <Group justify="space-between" align="flex-end" px="sm" py="xs">
                      <Stack
                        gap={2}
                        align="flex-start"
                        style={{ borderLeft: `4px solid ${color}`, paddingLeft: 8, borderRadius: 2 }}
                      >
                        <Text size="sm" fw={600} ta="left">
                          {topic.title}
                        </Text>
                        <Text size="xs" c={color} ta="left">
                          {topic.durationMinutes ?? 30} mins
                        </Text>
                        <Text size="xs" c="dimmed" ta="left" data-testid={`slots-per-day-${topic.id}`}>
                          {slotsPerDay(topic.durationMinutes ?? 30)} slot{slotsPerDay(topic.durationMinutes ?? 30) !== 1 ? 's' : ''}/day
                        </Text>
                      </Stack>
                      <Stack gap={2} align="flex-end">
                        <Text size="sm" fw={600} ta="right">
                          {topic.rate ? `£${topic.rate}` : 'Free'}
                        </Text>
                        <Text size="xs" c="dimmed" ta="right" data-testid={`total-slots-${topic.id}`}>
                          {totalSlots(topic.durationMinutes ?? 30)} total
                        </Text>
                      </Stack>
                    </Group>
                    {index === arr.length - 1 && <div style={{ height: 4 }} />}
                  </div>
                );
              })}
            <Divider />
            <Stack gap={2} px="sm" py="xs">
              <Text size="sm" fw={600} ta="left">Overall Total</Text>
              <Group justify="space-between" align="center" wrap="nowrap">
                <Text size="xs" c="dimmed" ta="left">Slots/day are per selected day; total is across all selected dates</Text>
                <Text size="xs" fw={700} c={primaryColor} style={{ flexShrink: 0 }} data-testid="grand-total-slots">
                  {grandTotalSlots} slots
                </Text>
              </Group>
            </Stack>
          </>
        )}
      </Paper>

      <TopicPickerModal
        opened={showTopicPicker}
        topics={topics}
        initialSelected={selectedTopicIds}
        onClose={() => setShowTopicPicker(false)}
        onConfirm={(ids) => {
          setSelectedTopicIds(ids);
          setShowTopicPicker(false);
        }}
      />

      <Button color={primaryColor} fullWidth disabled={!isValid || grandTotalSlots === 0} onClick={() => setShowReview(true)}>
        Continue
      </Button>

      <ReviewAvailabilityModal
        opened={showReview}
        onClose={() => setShowReview(false)}
        onConfirm={handleSubmit}
        isSubmitting={isSubmitting}
        topicSlots={topicSlots}
        grandTotalSlots={grandTotalSlots}
        startTime={startTime}
        endTime={endTime}
        activeDateTab={activeDateTab}
        specificDates={specificDates}
        recurringDays={recurringDays}
        recurringEndDate={recurringEndDate}
      />

      {toast && (
        <AppToast
          message={toast.message}
          variant={toast.variant}
          onClose={() => setToast(null)}
        />
      )}
    </Stack>
  );
};
